/**
 * Der Regal-Picker · Skript ansehen und sofort übernehmen, direkt vor dem Anruf.
 *
 * Die kompakte Fassung des Regals für den Moment, in dem jemand gleich telefoniert. Aufgerufen
 * aus `pages/SalesCrm.tsx`; die Auswahlmechanik dahinter ist dieselbe wie in `Regal.tsx`, und die
 * beiden Helfer dafür kommen von dort, damit ein Eintrag hier nicht anders zerlegt wird als eine
 * Zeile weiter.
 */

import { useEffect, useState } from 'react';
import { BookOpen, Download, Loader2, X } from 'lucide-react';
import { salesCrmApi } from '../../services/salesCrmApi';
import { librarySplit, nicheLabel } from './Regal';
import type { LibraryScript } from '../../types';

// Kompakter Bibliotheks-Picker direkt vor dem Call — Skript ansehen und sofort übernehmen.
export default function RegalPicker({
  niche,
  language,
  onAdopted,
  onClose,
  // Nur gesetzt, wenn dies als Dial-Gate vor einem Anruf steht (Punkt 52): macht per Beschriftung
  // klar, dass Schließen den Anruf NICHT abbricht — sonst liest sich das X wie „Anruf abbrechen".
  callGate,
}: {
  niche: string;
  language: string;
  onAdopted: () => void;
  onClose: () => void;
  callGate?: boolean;
}) {
  const [entries, setEntries] = useState<LibraryScript[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    salesCrmApi
      .listLibraryScripts({ niche: niche !== '_global' ? niche : undefined, language })
      .then(setEntries)
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false));
  }, [niche, language]);

  const adopt = async (entry: LibraryScript) => {
    setBusyId(entry.id);
    setErr(null);
    try {
      await salesCrmApi.adoptLibraryScript(entry.id, { niche, language });
      onAdopted();
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-bg-surface border border-border-primary rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border-primary">
          <div>
            <h3 className="text-sm font-semibold text-text-main flex items-center gap-2">
              <BookOpen size={16} className="text-accent" /> Skript aus der Bibliothek
            </h3>
            <p className="text-[11px] text-text-muted mt-0.5">
              {nicheLabel(niche)} · übernommenes Skript gilt ab dem nächsten Call
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {callGate && (
              <button
                onClick={onClose}
                className="px-2.5 py-1 text-[11px] text-text-muted hover:text-text-main border border-border-primary rounded-md whitespace-nowrap"
              >
                Ohne Wechsel anrufen
              </button>
            )}
            <button onClick={onClose} className="text-text-muted hover:text-text-main">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="p-4 overflow-y-auto space-y-2">
          {err && (
            <div className="text-[11px] text-danger bg-danger/10 border border-danger/30 rounded-md px-2 py-1">
              {err}
            </div>
          )}
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-text-muted py-4">
              <Loader2 size={14} className="animate-spin" /> Laden…
            </div>
          ) : entries.length === 0 ? (
            <p className="text-xs text-text-muted py-4">
              Für diese Nische gibt es noch keine geteilten Skripte.
            </p>
          ) : (
            entries.map((entry) => {
              const split = librarySplit(entry);
              const open = expandedId === entry.id;
              return (
                <div key={entry.id} className="border border-border-primary rounded-lg bg-bg-elevated/60">
                  <div className="p-3 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm text-text-main font-medium truncate">{entry.name}</div>
                      <div className="text-[11px] text-text-muted flex items-center gap-2 flex-wrap mt-0.5">
                        <span>von {entry.author ?? 'unbekannt'}</span>
                        {/* Vor dem Call ist „deckt es Einwände ab?" die zweite Frage nach „von wem?"
                            — sie hier zu beantworten kostet eine Zahl. */}
                        <span>· {split.flow.length} Blöcke</span>
                        {split.objections.length > 0 && (
                          <span className="text-amber-300/80">· {split.objections.length} Einwände</span>
                        )}
                        <span>· {entry.adopted_count} Übernahmen</span>
                        {/* wie oben: die Null benennen statt ausblenden */}
                        {entry.calls > 0 ? (
                          <span className="text-text-main">· {entry.conversion_pct}% ({entry.calls})</span>
                        ) : (
                          <span title="Noch keine Calls im Zeitraum einer Übernahme — es gibt nichts zu messen.">· keine Calls</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        onClick={() => setExpandedId(open ? null : entry.id)}
                        className="px-2 py-1 text-[11px] text-text-muted hover:text-text-main border border-border-primary rounded-md"
                      >
                        Ansehen
                      </button>
                      <button
                        onClick={() => adopt(entry)}
                        disabled={busyId === entry.id || entry.adopted_by_me}
                        className="px-2.5 py-1 bg-accent text-white rounded-md text-[11px] font-medium hover:bg-accent/90 disabled:opacity-40 flex items-center gap-1"
                      >
                        {busyId === entry.id ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                        {entry.adopted_by_me ? 'Aktiv' : 'Übernehmen'}
                      </button>
                    </div>
                  </div>
                  {open && (
                    <div className="border-t border-border-primary p-3 space-y-2 max-h-56 overflow-y-auto">
                      {[...split.flow, ...split.objections].map((key) => {
                        const sec = entry.sections_data[key];
                        if (!sec) return null;
                        const einwand = sec.kind === 'objection';
                        return (
                          <div key={key}>
                            <div className="text-[11px] font-medium text-text-main flex items-center gap-1.5">
                              {sec.title || key}
                              {/* Im engen Vorschaufenster ist eine eigene Rubrik zu viel Gerüst;
                                  die Marke am Titel sagt dasselbe in einem Wort. */}
                              {einwand && (
                                <span className="px-1 py-px rounded text-[9px] uppercase tracking-wider text-amber-300/90 border border-amber-400/30">
                                  Einwand
                                </span>
                              )}
                            </div>
                            <p className="text-[12px] leading-relaxed text-text-muted whitespace-pre-wrap">{sec.body}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

